import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { silentFetch } from '../data/apiClient';
import { useAuth } from './AuthContext';

const SubscriptionContext = createContext();

export function useSubscription() {
  return useContext(SubscriptionContext);
}

const CARD_LIMIT_FREE = 200;
const FREE_FREEZES_PER_WEEK = 1;

const DEFAULT_STATE = {
  tier: 'free',
  status: 'none',
  platform: 'none',
  isPremium: false,
  expiresAt: null,
  cardCount: 0,
};

export function SubscriptionProvider({ children }) {
  const { isAuthenticated } = useAuth();
  const [state, setState] = useState(DEFAULT_STATE);
  const [loading, setLoading] = useState(false);
  const [pricingTrigger, setPricingTrigger] = useState(null); // null | 'card_limit' | 'streak_freeze' | 'stats' | 'generic'

  const refreshSubscription = useCallback(async () => {
    if (!isAuthenticated) {
      setState(DEFAULT_STATE);
      return DEFAULT_STATE;
    }
    setLoading(true);
    // silentFetch — transient network failures on boot (auth race, CORS race,
    // offline) must NOT toast "Network error" at the user. Returns null on
    // failure; we keep the last good state in that case so a blip doesn't
    // appear to downgrade the user to FREE.
    const next = await silentFetch('/subscription');
    setLoading(false);
    if (!next) return state;
    const merged = { ...DEFAULT_STATE, ...next };
    setState(merged);
    return merged;
  }, [isAuthenticated, state]);

  useEffect(() => {
    if (isAuthenticated) {
      refreshSubscription();
    } else {
      setState(DEFAULT_STATE);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated]);

  const canAddCards = useCallback((currentCount) => {
    if (state.isPremium) return true;
    const count = typeof currentCount === 'number' ? currentCount : state.cardCount || 0;
    return count < CARD_LIMIT_FREE;
  }, [state.isPremium, state.cardCount]);

  const getCardUsage = useCallback((currentCount) => {
    const count = currentCount ?? state.cardCount ?? 0;
    if (state.isPremium) {
      return { current: count, limit: Infinity, percentage: 0, state: 'normal' };
    }
    const percentage = (count / CARD_LIMIT_FREE) * 100;
    let level = 'normal';
    if (percentage >= 100) level = 'critical';
    else if (percentage >= 80) level = 'warning';
    return { current: count, limit: CARD_LIMIT_FREE, percentage, state: level };
  }, [state.isPremium, state.cardCount]);

  const getFreezesAvailable = useCallback(() => {
    if (state.isPremium) return Infinity;
    return FREE_FREEZES_PER_WEEK;
  }, [state.isPremium]);

  const showPricing = useCallback((trigger = 'generic') => {
    setPricingTrigger(trigger);
  }, []);

  const dismissPricing = useCallback(() => setPricingTrigger(null), []);

  const value = {
    ...state,
    loading,
    pricingTrigger,
    canAddCards,
    getCardUsage,
    getFreezesAvailable,
    refreshSubscription,
    showPricing,
    dismissPricing,
  };

  return (
    <SubscriptionContext.Provider value={value}>
      {children}
    </SubscriptionContext.Provider>
  );
}
